/** Altura medida de um nível de pressão — função pura (testada em `km.test.ts`). */
import type { Nivel } from './types';

/**
 * Altura da linha de pressão `p`, em km, INTERPOLADA entre os níveis medidos.
 *
 * As alturas vêm do próprio perfil — a sondagem mediu a altura geopotencial de
 * cada nível, e calcular por atmosfera padrão erraria justamente onde a
 * atmosfera real difere dela.
 *
 * A interpolação é linear em log(p), que é como a altura varia com a pressão.
 * Uma versão anterior pegava o nível MAIS PRÓXIMO com 8% de tolerância, e na
 * base isso são 80 hPa: numa estação cujo nível mais baixo era 944 hPa, a
 * linha de 1000 hPa vinha rotulada "0,6 km" quando a altura ali é ~0,1 km.
 *
 * Fora da faixa medida devolve `null`: extrapolar inventaria altura.
 */
export function kmNoNivel(niveis: readonly Nivel[], p: number): number | null {
  const ordenados = [...niveis].filter((n) => n.p > 0 && Number.isFinite(n.z)).sort((a, b) => b.p - a.p);
  if (ordenados.length === 0) return null;

  const base = ordenados[0]!;
  const topo = ordenados[ordenados.length - 1]!;
  if (p > base.p || p < topo.p) return null;

  for (let i = 1; i < ordenados.length; i++) {
    const a = ordenados[i - 1]!;
    const b = ordenados[i]!;
    // A lista desce em pressão: procuramos o par que ENVOLVE `p`.
    if (p < b.p) continue;
    const span = Math.log(a.p) - Math.log(b.p);
    const k = span === 0 ? 0 : (Math.log(a.p) - Math.log(p)) / span;
    return (a.z + (b.z - a.z) * k) / 1000;
  }
  return topo.z / 1000;
}
